import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../css/EditSession.css';
import api from '../utils/axiosConfig';

const EditSession = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  
  const [collection, setCollection] = useState({
    assessmentTest: {
      xmlns: "http://www.imsglobal.org/xsd/imsqti_v2p1",
      "xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "xsi:schemaLocation": "http://www.imsglobal.org/xsd/imsqti_v2p1 imsqti_v2p1.xsd",
      identifier: "",
      title: "",
      metadata: {
        timeLimit: "20"
      },
      testPart: {
        identifier: "selfreport",
        navigationMode: "linear",
        submissionMode: "individual",
        assessmentSection: {
          identifier: "",
          title: "",
          itemRef: []
        }
      }
    }
  });

  const [newItem, setNewItem] = useState({
    category: "",
    identifier: "",
    href: ""
  });

  const [showPrecondition, setShowPrecondition] = useState(false);
  const [precondition, setPrecondition] = useState({
    match: {
      variable: {
        identifier: ""
      },
      baseValue: {
        baseType: "identifier",
        text: ""
      }
    }
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch session data on component mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true);
        //const response = await axios.get(`${API_BASE}/api/sessions/${sessionId}`);
        const response = await api.get(`/api/sessions/${sessionId}`);
        setCollection(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching session:', error);
        alert(`Failed to fetch session: ${error.response?.data?.message || error.message}`);
        setLoading(false);
        navigate('/dashboard/sessions');
      }
    };
    
    fetchSession();
  }, [sessionId, navigate]);

  // Handle session name change (sets both identifier and title)
  const handleSessionNameChange = (e) => {
    const { value } = e.target;
    setCollection(prev => {
      const updated = {
        ...prev,
        assessmentTest: {
          ...prev.assessmentTest,
          identifier: value,
          title: value,
          testPart: {
            ...prev.assessmentTest.testPart,
            assessmentSection: {
              ...prev.assessmentTest.testPart.assessmentSection,
              identifier: value,
              title: value
            }
          }
        }
      };
      return updated;
    });
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreconditionChange = (e) => {
    const { name, value } = e.target;
    if (name === "variableIdentifier") {
      setPrecondition(prev => ({
        ...prev,
        match: {
          ...prev.match,
          variable: {
            identifier: value
          }
        }
      }));
    } else {
      setPrecondition(prev => ({
        ...prev,
        match: {
          ...prev.match,
          baseValue: {
            ...prev.match.baseValue,
            text: value
          }
        }
      }));
    }
  };

  const addItem = () => {

    const trimmedIdentifier = newItem.identifier.trim();
    const trimmedCategory = newItem.category.trim();


    // Validate required fields
    if (!trimmedCategory || !trimmedIdentifier) {
      setErrorMessage("Required fields are missing.");
      return;
    }
    
    setErrorMessage("");

     // Optional: prevent spaces inside identifier
    if (/\s/.test(trimmedIdentifier)) {
      setErrorMessage("Identifier must not contain spaces.");
      return;
    }

    // Only include preCondition if it has values
    const itemToAdd = {
      category: trimmedCategory,
      identifier: trimmedIdentifier,
      href: `${trimmedIdentifier}.xml`
    };


    if (
    showPrecondition &&
    precondition.match.variable.identifier.trim() &&
    precondition.match.baseValue.text.trim()
    ) {
      itemToAdd.preCondition = {
        match: {
          variable: {
            identifier: precondition.match.variable.identifier.trim()
          },
          baseValue: {
            baseType: "identifier",
            text: precondition.match.baseValue.text.trim()
          }
        }
      };
    }

    setCollection(prev => {
      const newItemRef = [...prev.assessmentTest.testPart.assessmentSection.itemRef, itemToAdd];
      return {
        ...prev,
        assessmentTest: {
          ...prev.assessmentTest,
          testPart: {
            ...prev.assessmentTest.testPart,
            assessmentSection: {
              ...prev.assessmentTest.testPart.assessmentSection,
              itemRef: newItemRef
            }
          }
        }
      };
    });

    // Reset form
    setNewItem({
      category: "",
      identifier: ""
    });
    setPrecondition({
      match: {
        variable: {
          identifier: ""
        },
        baseValue: {
          baseType: "identifier",
          text: ""
        }
      }
    });
    setShowPrecondition(false);
  };

  const deleteItem = (index) => {
    setCollection(prev => {
      const updatedItemRef = [...prev.assessmentTest.testPart.assessmentSection.itemRef];
      updatedItemRef.splice(index, 1);
      
      return {
        ...prev,
        assessmentTest: {
          ...prev.assessmentTest,
          testPart: {
            ...prev.assessmentTest.testPart,
            assessmentSection: {
              ...prev.assessmentTest.testPart.assessmentSection,
              itemRef: updatedItemRef
            }
          }
        }
      };
    });
  };

  const moveItem = (index, direction) => {
    setCollection(prev => {
      const items = [...prev.assessmentTest.testPart.assessmentSection.itemRef];
      if (direction === 'up' && index > 0) {
        [items[index], items[index - 1]] = [items[index - 1], items[index]];
      } else if (direction === 'down' && index < items.length - 1) {
        [items[index], items[index + 1]] = [items[index + 1], items[index]];
      }
      
      return {
        ...prev,
        assessmentTest: {
          ...prev.assessmentTest,
          testPart: {
            ...prev.assessmentTest.testPart,
            assessmentSection: {
              ...prev.assessmentTest.testPart.assessmentSection,
              itemRef: items
            }
          }
        }
      };
    });
  };

  const updateSession = async () => {
    if (!collection.assessmentTest.identifier) {
      alert('Please enter a Session Name');
      return;
    }

    try {
      // Create clean collection without empty preConditions and duplicate metadata
      const collectionToSave = {
        ...collection,
        assessmentTest: {
          ...collection.assessmentTest,
          testPart: {
            ...collection.assessmentTest.testPart,
            assessmentSection: {
              ...collection.assessmentTest.testPart.assessmentSection,
              itemRef: collection.assessmentTest.testPart.assessmentSection.itemRef.map(item => {
                const cleanItem = { ...item };
                if (cleanItem.preCondition && 
                    (!cleanItem.preCondition.match.variable.identifier || 
                     !cleanItem.preCondition.match.baseValue.text)) {
                  delete cleanItem.preCondition;
                }
                return cleanItem;
              })
            }
          }
        }
      };

      // Use the session identifier as the collection name
      const collectionName = collection.assessmentTest.identifier;
      
      // Update existing session
      await api.put(`/api/sessions/${sessionId}`, {
        collectionName,
        data: collectionToSave
      });
      
      alert(`Session "${collectionName}" updated successfully!`);
      navigate('/dashboard/sessions');
    } catch (error) {
      console.error('Error updating session:', error);
      alert(`Failed to update session: ${error.response?.data?.message || error.message}`);
    }
  };

  const cleanCollectionForDisplay = () => {
    const displayCollection = JSON.parse(JSON.stringify(collection));
    
    // Remove metadata from testPart if it exists
    if (displayCollection.assessmentTest.testPart.metadata) {
      delete displayCollection.assessmentTest.testPart.metadata;
    }
    
    // Clean items with empty preConditions
    displayCollection.assessmentTest.testPart.assessmentSection.itemRef = 
      displayCollection.assessmentTest.testPart.assessmentSection.itemRef.map(item => {
        const cleanItem = { ...item };
        if (cleanItem.preCondition && 
            (!cleanItem.preCondition.match.variable.identifier || 
             !cleanItem.preCondition.match.baseValue.text)) {
          delete cleanItem.preCondition;
        }
        return cleanItem;
      });
    
    return displayCollection;
  };

  if (loading) {
    return <div className="session-creator">Loading session data...</div>;
  }

  return (
    <div className="session-creator">
      <h2>Edit Session</h2>
      
      <div className="session-info">
        <div>
          <label>Session Name:
            <span style={{ fontWeight: "normal", fontSize: "12px", marginLeft: "8px", color: "#666" }}>
            (Session name should always start with "session", e.g. sessionDaily)
            </span>
          </label>
          <input
            type="text"
            name="sessionName"
            value={collection.assessmentTest.identifier}
            onChange={handleSessionNameChange}
            required
          />
        </div>
        <div>
          <label>Time Limit (minutes):</label>
          <input
            type="number"
            value={collection.assessmentTest.metadata.timeLimit}
            onChange={(e) => setCollection(prev => ({
              ...prev,
              assessmentTest: {
                ...prev.assessmentTest,
                metadata: {
                  timeLimit: e.target.value
                }
              }
            }))}
          />
        </div>
      </div>

      <div className="item-creator">
        <h3>Add Items</h3>
        <div>
          <label>Category:</label>
          <select
            name="category"
            value={newItem.category}
            onChange={handleItemChange}
          >
            <option value="">Select category</option>
            <option value="declaration">Declaration</option>
            <option value="singlechoiceh">Single Choice Horizontal</option>
            <option value="singlechoice">Single Choice Vertical</option>
            <option value="singlechoiceb">Single Choice Button</option>
            <option value="textentry">Text Entry</option>
            <option value="feedback">Feedback</option>
          </select>
        </div>
        <div>
          <label>Identifier:</label>
          <input
            type="text"
            name="identifier"
            value={newItem.identifier}
            onChange={handleItemChange}
            required
          />
        </div>
        {/* <div>
          <label>Href (XML file):</label>
          <input
            type="text"
            name="href"
            value={newItem.href}
            onChange={handleItemChange}
            required
          />
        </div> */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div>
          <label>
            <input
              type="checkbox"
              checked={showPrecondition}
              onChange={() => setShowPrecondition(!showPrecondition)}
            />
            Add Precondition
          </label>
        </div>

        {showPrecondition && (
          <div className="precondition-form">
            <h4>Precondition</h4>
            <div>
              <label>Variable Identifier:</label>
              <input
                type="text"
                name="variableIdentifier"
                value={precondition.match.variable.identifier}
                onChange={handlePreconditionChange}
              />
            </div>
            <div>
              <label>Match Value:</label>
              <input
                type="text"
                name="baseValueText"
                value={precondition.match.baseValue.text}
                onChange={handlePreconditionChange}
              />
            </div>
          </div>
        )}

        <button onClick={addItem}>Add Item</button>
      </div>

      <div className="item-list">
        <h3>Current Items ({collection.assessmentTest.testPart.assessmentSection.itemRef.length})</h3>
        {collection.assessmentTest.testPart.assessmentSection.itemRef.length === 0 ? (
          <p>No items added yet</p>
        ) : (
          <ul>
            {collection.assessmentTest.testPart.assessmentSection.itemRef.map((item, index) => (
              <li key={index}>
                <div className="item-content">
                  <span>{item.identifier} ({item.category}) - {item.href}</span>
                  {item.preCondition && (
                    <span className="precondition-info">
                      - Precondition: {item.preCondition.match.variable.identifier} = {item.preCondition.match.baseValue.text}
                    </span>
                  )}
                </div>
                <div className="item-actions">
                  <button onClick={() => moveItem(index, 'up')} disabled={index === 0}>↑</button>
                  <button onClick={() => moveItem(index, 'down')} disabled={index === collection.assessmentTest.testPart.assessmentSection.itemRef.length - 1}>↓</button>
                  <button onClick={() => deleteItem(index)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <button onClick={updateSession} className="save-btn">
        Update Session
      </button>

      <div className="json-preview">
        <h3>JSON Preview</h3>
        <pre>{JSON.stringify(cleanCollectionForDisplay(), null, 2)}</pre>
      </div>
    </div>
  );
};

export default EditSession;